import React, { useState, useEffect } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, TablePagination, Box, Typography, IconButton, Button, Stack,
  Select, MenuItem, FormControl, InputLabel, Chip
} from "@mui/material";
import { Visibility, Delete } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../axiosInstance";
import { toast } from "react-toastify";

const apiUrl = process.env.REACT_APP_API_URL;

const SFTPCronListPage = () => {
  const [crons, setCrons] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [role, setRole] = useState("");
  const [cronStatus, setCronStatus] = useState(""); // Filter for cron status
  const [vendorFilter, setVendorFilter] = useState(""); // Filter for vendor ID
  const [vendors, setVendors] = useState([]); // Store vendors for dropdown
  const [allPaused, setAllPaused] = useState(false); // track current state

  const handleToggleAllCrons = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const action = allPaused ? "resume" : "pause";
  
      const response = await axiosInstance.post(`${apiUrl}/dropbox-crons/toggle`, { action }, {
        headers: { Authorization: `Bearer ${token}` },
      });
  
      toast.success(response.data.message);
      setAllPaused(!allPaused); // flip state
      fetchCrons(); // refresh data
    } catch (error) {
      toast.error("Failed to toggle cron jobs");
      console.error("Toggle error:", error);
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    fetchCrons();
  }, [page, rowsPerPage, search, cronStatus, vendorFilter]);

  useEffect(() => {
    fetchVendors(); // Fetch vendors when the component mounts
  }, []);

  const fetchCrons = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axiosInstance.get(`${apiUrl}/sftp-crons/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: search,
          status: cronStatus,
          vendorId: vendorFilter
        },
      });

      setCrons(response.data.crons);
      setRole(response.data.role);
      setTotalRows(response.data.total);
    } catch (error) {
      console.error("Error fetching crons:", error);
    }
  };

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axiosInstance.get(`${apiUrl}/vendor/list?fetchAll=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setVendors(response.data.vendors);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewCron = (id) => {
    navigate(`/connection/sftp/${id}`);
  };

  const handleDeleteCron = async (id) => {
    if (!window.confirm("Are you sure you want to delete this cron?")) return;

    try {
      const token = localStorage.getItem("authToken");
      await axiosInstance.delete(`${apiUrl}/sftp-cron/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Cron deleted successfully!");
      fetchCrons(); // Refresh list
    } catch (error) {
      toast.error("Failed to delete cron");
      console.error("Error deleting cron:", error);
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Cron List
      </Typography>

      {/* Search & Filters */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          label="Search Cron"
          variant="outlined"
          fullWidth
          margin="normal"
          value={search}
          onChange={handleSearchChange}
        />

        {/* Cron Status Filter */}
        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={cronStatus}
            onChange={(e) => setCronStatus(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="1">Active</MenuItem>
            <MenuItem value="0">Inactive</MenuItem>
          </Select>
        </FormControl>

        {/* Vendor Filter (Only for Admin) */}
        {role === "Admin" && (
          <FormControl fullWidth>
            <InputLabel>Vendor</InputLabel>
            <Select
              value={vendorFilter}
              onChange={(e) => setVendorFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {vendors.map((vendor) => (
                <MenuItem key={vendor.vendorId} value={vendor.vendorId}>
                  {vendor.username}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

         <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleToggleAllCrons}
                  sx={{ minWidth: 200 }}
                >
                  {allPaused ? "Resume All Crons" : "Pause All Crons"}
                </Button>
        

        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/connection/ftp")}
          sx={{ minWidth: 200 }} // Ensures a reasonable button width

        >
          Add SFTP Connection
        </Button>
      </Stack>

      {/* Cron Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {role === "Admin" && <TableCell sx={{ fontWeight: "bold" }}>Vendor ID</TableCell>}
              {role === "Admin" && <TableCell sx={{ fontWeight: "bold" }}>Vendor Name</TableCell>}
              <TableCell sx={{ fontWeight: "bold" }}>Link</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Cron Expression</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>VPC Col</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Stock Col</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Cost Col</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Created At</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {crons.length > 0 ? (
              crons.map((cron) => (
                <TableRow key={cron.id}>
                  {role === "Admin" && <TableCell>{cron.vendorId || "N/A"}</TableCell>}
                      {role === "Admin" && <TableCell>{cron.vendorName || "N/A"}</TableCell>}
                  <TableCell>{cron.host}</TableCell>
                  <TableCell>{cron.cronExpression}</TableCell>
                  <TableCell>{cron.vpcCol || "N/A"}</TableCell>
                  <TableCell>{cron.stockCol || "N/A"}</TableCell>
                  <TableCell>{cron.costCol || "N/A"}</TableCell>
                   <TableCell>
                                    <Chip
                                      label={cron.active ? "Active" : "Paused"}
                                      color={cron.active ? "success" : "error"}
                                      size="small"
                                    
                                    />
                                  </TableCell>
                  <TableCell>{cron.createdAt}</TableCell>
                  <TableCell>
                    {/* View Button */}
                    <IconButton color="primary" onClick={() => handleViewCron(cron.id)}>
                      <Visibility />
                    </IconButton>

                    {/* Delete Button */}
                    <IconButton color="error" onClick={() => handleDeleteCron(cron.id)}>
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={role === "Admin" ? 8 : 7} align="center">
                  No crons found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={totalRows}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
};

export default SFTPCronListPage;
